import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Layers, TrendingUp, Clock, Loader2, Download, Trophy, ThumbsDown, BarChart2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Legend, Cell,
  LineChart, Line,
} from "recharts";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KpiCard({ icon: Icon, title, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "18" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function CohortReport({ filterHook }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterHook.buildApiFilters();
      const res = await axios.post(`${API}/reports/cohort`, { filters }, { withCredentials: true });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [filterHook]);

  useEffect(() => { fetchData(); }, [filterHook.filters.pipelineIds, filterHook.filters.responsavelIds]); // eslint-disable-line

  const exportExcel = () => {
    if (!data?.cohorts) return;
    const ws = XLSX.utils.json_to_sheet(data.cohorts.map(c => ({
      "Mês": c.month_label, "Criados": c.created, "Ganhos": c.ganho, "Perdidos": c.perdido,
      "Em Aberto": c.aberto, "Taxa Conversão %": c.taxa_conversao, "Taxa Perda %": c.taxa_perda,
      "MRR Ganho": c.mrr_ganho, "Dias Médios p/ Fechar": c.avg_days_to_close ?? "—",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cohort");
    XLSX.writeFile(wb, `cohort_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && !data) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>;
  if (!data?.cohorts?.length) return <p className="text-sm text-slate-400">Sem dados suficientes para análise de cohort.</p>;

  const cohorts = data.cohorts;
  const totalCreated = cohorts.reduce((s, c) => s + c.created, 0);
  const totalGanho = cohorts.reduce((s, c) => s + c.ganho, 0);
  const totalPerdido = cohorts.reduce((s, c) => s + c.perdido, 0);
  const avgConversao = totalCreated > 0 ? ((totalGanho / totalCreated) * 100).toFixed(1) : "0";
  const avgDays = cohorts.filter(c => c.avg_days_to_close).reduce((s, c) => s + c.avg_days_to_close, 0) / Math.max(cohorts.filter(c => c.avg_days_to_close).length, 1);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Layers} title="Total Criados" value={totalCreated} sub="todos os meses" color={tc500} />
        <KpiCard icon={Trophy} title="Total Ganhos" value={totalGanho} sub={`${avgConversao}% de conversão`} color="#10B981" />
        <KpiCard icon={ThumbsDown} title="Total Perdidos" value={totalPerdido} sub={`${totalCreated > 0 ? ((totalPerdido / totalCreated) * 100).toFixed(1) : 0}% de perda`} color="#EF4444" />
        <KpiCard icon={Clock} title="Tempo Médio" value={`${avgDays.toFixed(0)}d`} sub="criação → ganho" color="#8B5CF6" />
      </div>

      {/* Stacked bar: created vs ganho vs perdido vs aberto */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" style={{ color: tc500 }} />Cohort por Mês</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={cohorts}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month_label" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <RT contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="ganho" name="Ganhos" stackId="a" fill="#10B981" />
            <Bar dataKey="perdido" name="Perdidos" stackId="a" fill="#EF4444" />
            <Bar dataKey="aberto" name="Em Aberto" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Line chart: conversion rate over time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: "#10B981" }} />Taxa de Conversão por Mês</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={cohorts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month_label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="%" domain={[0, "auto"]} />
              <RT contentStyle={{ fontSize: 12 }} formatter={v => `${v}%`} />
              <Line type="monotone" dataKey="taxa_conversao" name="Conversão %" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="taxa_perda" name="Perda %" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: "#8B5CF6" }} />Velocidade de Fechamento</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={cohorts.filter(c => c.avg_days_to_close)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month_label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="d" />
              <RT contentStyle={{ fontSize: 12 }} formatter={v => `${v} dias`} />
              <Bar dataKey="avg_days_to_close" name="Dias médios" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-700">Detalhamento por Cohort</h3>
          <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600 h-8 text-xs"><Download className="w-3.5 h-3.5" />Exportar</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-y border-slate-100 bg-slate-50/50">
              <tr>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Mês</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Criados</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Ganhos</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Perdidos</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Abertos</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Conversão</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Perda</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-right">MRR Ganho</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase text-center">Dias p/ Fechar</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map(c => (
                <tr key={c.month} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800">{c.month_label}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 text-center">{c.created}</td>
                  <td className="px-3 py-2 text-xs text-emerald-600 font-medium text-center">{c.ganho}</td>
                  <td className="px-3 py-2 text-xs text-rose-500 text-center">{c.perdido}</td>
                  <td className="px-3 py-2 text-xs text-amber-500 text-center">{c.aberto}</td>
                  <td className="px-3 py-2 text-xs text-center">
                    <span className={`font-medium ${c.taxa_conversao >= 30 ? "text-emerald-600" : c.taxa_conversao >= 15 ? "text-amber-600" : "text-rose-500"}`}>{c.taxa_conversao}%</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400 text-center">{c.taxa_perda}%</td>
                  <td className="px-3 py-2 text-xs text-slate-600 text-right">R$ {(c.mrr_ganho || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 text-center">{c.avg_days_to_close != null ? `${c.avg_days_to_close}d` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
